import { useEffect, useRef, useState } from "react";
import { toast } from "../hooks/useToast";
import { storage } from "../firebase";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { arrayUnion, arrayRemove } from "firebase/firestore";
import {C, withAlpha } from "../constants/theme";
import { fbUpdate } from "../hooks/useFirebase";
import { Btn } from "./ui/Btn";
import { extrairFoto3mf, extrairPlacas3mf } from "../utils/extract3mf";
import { ehGcodeDisfarcadoDe3mf, temArquivo3mfValido, abrirNoBambuStudio, baixarArquivo } from "../utils/arquivos3d";
import { useAvisoAoSair } from "../hooks/useAvisoAoSair";
import { Configurar3D } from "./Configurar3D";
import { prepararArquivo3d } from "../utils/visualizacao3d";

export function ArquivoCard({ produto, onAtualizar }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  useAvisoAoSair(uploading);
  const arquivos = produto.arquivos || [];

  // Migração automática e silenciosa: um .3mf enviado ANTES da galeria de
  // placas existir não tem `placasVerificado`. Ao abrir o produto, baixa
  // cada um de novo e roda a mesma extração do upload novo — sem botão,
  // sem confirmação. Roda uma vez por produto aberto; se algum arquivo
  // falhar (rede etc.), tenta de novo na próxima vez que o produto abrir.
  useEffect(() => {
    const pendentes = arquivos.filter(a => a.nome?.toLowerCase().endsWith(".3mf") && !a.placasVerificado);
    if (!pendentes.length) return;
    let cancelado = false;
    (async () => {
      const atualizados = await Promise.all(arquivos.map(async (a) => {
        if (!a.nome?.toLowerCase().endsWith(".3mf") || a.placasVerificado) return a;
        try {
          const resp = await fetch(a.url);
          if (!resp.ok) throw new Error("HTTP " + resp.status);
          const blob = await resp.blob();
          const placas = await extrairPlacas3mf(blob);
          return { ...a, placasVerificado: true, ...(placas?.length ? { placas } : {}) };
        } catch {
          return a; // não marca placasVerificado — tenta de novo na próxima vez
        }
      }));
      if (!cancelado) {
        await fbUpdate("produtos", produto.id, { arquivos: atualizados }).catch(() => {});
        onAtualizar();
      }
    })();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [produto.id]);

  const uploadArquivos = async (filesSelecionados) => {
    setUploading(true);
    try {
      // Corrige o nome de gcode que na verdade é um .3mf disfarçado (Bambu Studio
      // às vezes exporta assim) — precisa saber isso ANTES de decidir bloquear
      // gcodes puros, senão a ordem de seleção dos arquivos importaria. .zip
      // sobe inteiro, sem extrair o conteúdo.
      const pendentes = await Promise.all(filesSelecionados.map(async (f) => {
        const ext = f.name.split(".").pop().toLowerCase();
        if (ext === "gcode" && await ehGcodeDisfarcadoDe3mf(f)) {
          return new File([f], f.name.replace(/\.gcode$/i, ".3mf"), { type: f.type });
        }
        return f;
      }));

      const temAlgum3mf = temArquivo3mfValido(arquivos) || pendentes.some(f => f.name.toLowerCase().endsWith(".3mf"));

      const prontos = [];
      for (const f of pendentes) {
        const ext = f.name.split(".").pop().toLowerCase();
        if (ext === "gcode") {
          // G-code puro (sem projeto) não dá pra alterar depois — só aceita
          // se já existe (ou está entrando junto) um .3mf de verdade.
          if (!temAlgum3mf) {
            toast(`"${f.name}" é um G-code já fatiado, sem o projeto — não dá pra alterar depois. Envie também o .3mf usado pra fatiar (junto ou antes) pra poder salvar.`, "warning");
            continue;
          }
        } else if (f.type.startsWith("video/") || f.type.startsWith("image/")) {
          // Confusão comum: aqui é só arquivo de impressão 3D. Foto/vídeo do
          // produto sobe no campo "FOTOS E VÍDEOS", mais acima na tela.
          toast(`"${f.name}" é vídeo/foto — aqui é só pra arquivo de impressão 3D. Suba vídeo/foto no campo "FOTOS E VÍDEOS" mais acima.`, "warning");
          continue;
        } else if (!["stl","3mf","obj","zip"].includes(ext)) {
          toast(`"${f.name}": formato não suportado (use STL, GCODE, 3MF, OBJ ou ZIP).`, "warning");
          continue;
        }
        prontos.push(f);
      }
      if (prontos.length === 0) { setUploading(false); return; }

      const novos = []; // só as entradas NOVAS deste lote — vai por arrayUnion, não sobrescreve o array
      let novaFotoStorage = null; // {path,url} — thumbnail do 3mf, subido pro Storage
      let novaFotoBase64 = null; // fallback (base64 cru) só se o upload acima falhar
      // "i" no nome evita colisão de path quando vários arquivos são enviados
      // juntos rápido demais pro Date.now() sozinho diferenciar (o upload
      // seguinte sobrescreveria o do anterior no mesmo path).
      for (let i = 0; i < prontos.length; i++) {
        const file = prontos[i];
        const path = `produtos/${produto.id}/${Date.now()}_${i}_${file.name}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        const entrada = await prepararArquivo3d(file, { nome: file.name, path, url });

        // Projeto multi-placa (Bambu Studio/OrcaSlicer) — guarda a miniatura +
        // nome de cada peça pra mostrar como galeria em vez de 1 linha só.
        // placasVerificado marca que esse arquivo já passou por aqui — a
        // migração de arquivos antigos (Produtos.jsx) usa essa flag pra pular
        // upload novo e só reprocessar o que ficou de antes desse recurso existir.
        if (file.name.toLowerCase().endsWith(".3mf")) {
          const placas = await extrairPlacas3mf(file);
          if (placas?.length) entrada.placas = placas;
          entrada.placasVerificado = true;
        }
        novos.push(entrada);

        // O thumbnail embutido no .3mf vira a capa (fotoBase64) do produto
        // enquanto ele não tem foto nenhuma — nunca entra no carretel de
        // fotos. Só do primeiro .3mf do lote.
        if (file.name.toLowerCase().endsWith(".3mf") && !novaFotoStorage) {
          const thumb = await extrairFoto3mf(file);
          if (thumb) {
            try {
              const blob = await (await fetch(thumb)).blob();
              const fotoPath = `produtos/${produto.id}/${Date.now()}_thumb-3mf.jpg`;
              const fotoStorageRef = ref(storage, fotoPath);
              await uploadBytes(fotoStorageRef, blob);
              const fotoUrl = await getDownloadURL(fotoStorageRef);
              novaFotoStorage = { path: fotoPath, url: fotoUrl };
              novaFotoBase64 = thumb;
            } catch {}
          }
        }
      }

      // arrayUnion é atômico no servidor — não corre risco de sobrescrever
      // com uma lista de arquivos desatualizada se algo mais salvar o produto
      // no meio do upload.
      const patch = { arquivos: arrayUnion(...novos) };
      // O thumbnail do .3mf vira a capa (fotoBase64) só enquanto o produto
      // ainda não tem NENHUMA foto de verdade — nunca entra no carretel de
      // fotos (array `fotos`), que fica reservado pro que o vendedor
      // adiciona manualmente. Assim que a 1ª foto real entrar na galeria,
      // ela substitui esse thumbnail como capa (ver Produtos.jsx).
      if (novaFotoStorage && !produto.fotoBase64 && !(produto.fotos?.length > 0)) {
        patch.fotoBase64 = novaFotoStorage.url || novaFotoBase64;
      }
      await fbUpdate("produtos", produto.id, patch);
      onAtualizar();
      toast(prontos.length > 1 ? `${prontos.length} arquivos adicionados ✓` : "Arquivo adicionado ✓", "success");
    } catch(e) { toast("Erro no upload: " + e.message, "error"); }
    setUploading(false);
  };

  const delArquivo = async (arq) => {
    if (!window.confirm("Excluir " + arq.nome + "?")) return;
    try { await deleteObject(ref(storage, arq.path)); } catch {}
    await fbUpdate("produtos", produto.id, { arquivos: arrayRemove(arq), ...(produto.visualizacao3d?.url === arq.url ? { visualizacao3d: null } : {}) });
    onAtualizar();
  };

  const iconeExt = (nome) => {
    const ext = nome.split(".").pop().toLowerCase();
    if (ext === "stl") return "🔷";
    if (ext === "gcode") return "⚙️";
    if (ext === "3mf") return "📦";
    if (ext === "zip") return "🗜️";
    return "📄";
  };

  return (
    <div style={{ borderTop:"1px solid "+C.border, paddingTop:10, marginTop:8 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
        <div style={{ color:"#f59e0b", fontSize:11, fontWeight:700, letterSpacing:1 }}>📁 ARQUIVOS 3D</div>
        <div>
          <input ref={fileRef} type="file" accept=".stl,.gcode,.3mf,.obj,.zip" multiple style={{ display:"none" }}
            onChange={e => { const files=[...e.target.files]; if (files.length) uploadArquivos(files); e.target.value=""; }} />
          <Btn small color="#f59e0b" onClick={() => fileRef.current.click()} style={{ opacity:uploading?0.6:1 }}>
            {uploading ? "⏳" : "⬆ Upload"}
          </Btn>
        </div>
      </div>
      {arquivos.length === 0 && (
        <div style={{ color:C.muted, fontSize:11, padding:"6px 0" }}>Nenhum arquivo enviado</div>
      )}
      {arquivos.length > 0 && !temArquivo3mfValido(arquivos) && (
        <div style={{ color:"#f59e0b", fontSize:11, padding:"4px 0 8px" }}>⚠️ Sem .3mf anexado — não dá pra reimprimir/alterar essa peça depois.</div>
      )}
      {arquivos.map(arq => {
        const eh3mf = arq.nome.toLowerCase().endsWith(".3mf");
        return (
          <div key={arq.path}>
            <div style={{ display:"flex", alignItems:"center", gap:6, padding:"5px 0", borderBottom: "1px solid "+withAlpha(C.border, "55") }}>
              <span style={{ fontSize:14 }}>{iconeExt(arq.nome)}</span>
              <span style={{ color:C.text, fontSize:12, flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{arq.nome}</span>
              {eh3mf && window.electronAPI?.isElectron && (
                <button onClick={() => abrirNoBambuStudio(arq.url, arq.nome)} title="Abrir no Bambu Studio"
                  style={{ background:"#f59e0b22", border:"1px solid #f59e0b44", borderRadius:6, color:"#f59e0b", cursor:"pointer", padding:"3px 8px", fontSize:12, fontWeight:700 }}>🧊</button>
              )}
              <button onClick={() => baixarArquivo(arq.url, arq.nome)} style={{ background:withAlpha(C.accent2, "22"), border:"1px solid "+withAlpha(C.accent2, "44"), borderRadius:6, color:C.accent2, cursor:"pointer", padding:"3px 8px", fontSize:12, fontWeight:700 }}>⬇</button>
              <button onClick={() => delArquivo(arq)} style={{ background:withAlpha(C.danger, "22"), border:"1px solid "+withAlpha(C.danger, "44"), borderRadius:6, color:C.danger, cursor:"pointer", padding:"3px 8px", fontSize:12, fontWeight:700 }}>🗑</button>
            </div>
            {eh3mf && <Configurar3D arquivo={arq} />}

          </div>
        );
      })}
    </div>
  );
}
